import { Form, Select, Space } from "antd";
import React, { type FC, use, useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { GroupFormContext } from "@dms/modules/GroupFormModules/context/GroupFormContext";
import { FormWrapperContext } from "@dms/modules/DocumentFormModule/context/FormWrapperContext";
import { SingleFormContext } from "@dms/modules/SingleFormModule/context/SingleFormContext";
import { validation } from "../../../../scripts/infrastructure/helpers/validation";
import { BaseInput } from "@app/components/shared/form/baseComponents";
import { Contacts, GQL } from "@binale-tech/shared";
import { useApolloClient } from "@apollo/client";
import { CompanyContext } from "../../../../scripts/context/CompanyContext";
import { useEuroCountryArray } from "../../../../scripts/infrastructure/hooks/useEuroCountryArray";

interface IProps {
    disabled?: boolean;
}

export const DocumentVatField: FC<IProps> = ({ disabled }) => {
    const intl = useIntl();
    const { isGroupEditor } = use(FormWrapperContext);
    const { diverseValueFields } = use(GroupFormContext);
    const placeholder = isGroupEditor && diverseValueFields?.includes("UStIdNr") ? "Diverse" : undefined;
    const { isReadOnly } = use(SingleFormContext);
    const client = useApolloClient();
    const euroCountriesArray = useEuroCountryArray();
    const { companyGQL } = use(CompanyContext);
    const form = Form.useFormInstance<GQL.IDocumentUpdateInput>();
    const landCode = Form.useWatch("landCode", form);
    const landCodeRef = useRef(landCode);

    useEffect(() => {
        if (landCodeRef.current === landCode) {
            return;
        }
        if (landCodeRef.current) {
            // only reset field if there was already a value in ref to prevent cleanup on mount
            form.setFields([
                {
                    name: "UStIdNr",
                    errors: [],
                    value: "",
                },
            ]);
        }
        landCodeRef.current = landCode;
    }, [form, landCode]);

    return (
        <Space.Compact>
            <Form.Item<GQL.IDocumentUpdateInput>
                label={<FormattedMessage id="app.fields.euroCountryCode" />}
                name={"landCode"}
            >
                <Select options={euroCountriesArray} disabled={isReadOnly || disabled} allowClear />
            </Form.Item>

            <Form.Item<GQL.IDocumentUpdateInput>
                label={<FormattedMessage id="app.fields.vatNb" />}
                name="UStIdNr"
                validateDebounce={200}
                hasFeedback
                rules={[
                    validation.vat(client, landCode, companyGQL.id),
                    validation.max(intl, Contacts.ContactConstants.getVatLengthMax(landCode)),
                ]}
            >
                <BaseInput
                    placeholder={placeholder}
                    maxLength={Contacts.ContactConstants.getVatLengthMax(landCode)}
                    showCount
                    disabled={isReadOnly || !landCode || disabled}
                />
            </Form.Item>
        </Space.Compact>
    );
};
